import { Mutex } from "async-mutex";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect, useMemo } from "react";
import { mapValues } from "remeda";
import { useDebounceCallback } from "usehooks-ts";
import { sendLoggingEvents, TrackingEvents } from "../events";
import { toast } from "../toasts";
import type {
  RemoteParticipant,
  RoomEvent,
  RoomEventHandlerMap,
} from "../video-sdk";
import {
  hasParticipant,
  participantsAtom,
  remoteParticipantToDiagnosticInfo,
  roomAtom,
  roomToDiagnosticInfo,
} from "../video-sdk";
import { dominantSpeakerIdAtom } from "../video-sdk/twilio";
import { useDisconnectRoomCallback } from "./useDisconnectRoomCallback";

const RECONNECT_TOAST_KEY = "auto-reconnect-toast";

type RoomDisconnectedEventHandler = RoomEventHandlerMap["disconnected"];
type RoomParticipantDisconnectedEventHandler =
  RoomEventHandlerMap["participantDisconnected"];
type RoomParticipantConnectedEventHandler =
  RoomEventHandlerMap["participantConnected"];
type RoomReconnectingEventHandler = RoomEventHandlerMap["reconnecting"];

type UseRoomProps = {
  onRoomDisconnect?: RoomDisconnectedEventHandler;
  onParticipantDisconnect?: RoomParticipantDisconnectedEventHandler;
  /**
   * Called only when a participant first connects
   */
  onParticipantInitialConnect?: RoomParticipantConnectedEventHandler;
  onParticipantConnect?: RoomParticipantConnectedEventHandler;
  onPageUnload?: () => void;
};

export const RAPID_RECONNECT_THRESHOLD = 1000;
export const CHECK_FOR_EXISTING_PARTICIPANTS_INTERVAL = 3000;

function filterOutByParticipantId(
  participants: RemoteParticipant[],
  id: string,
) {
  return participants.filter((p) => p.identity !== id);
}

export const roomEventListenerMutex = new Mutex();

/**
 * Wraps the given event handlers in the given mutex, ensuring that only one
 * handler can run at a time.
 */
export function mutexifyEventHandlers(
  handlers: RoomEventHandlerMap,
): RoomEventHandlerMap {
  return mapValues(
    handlers,
    (handler) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (...args: any[]) =>
        roomEventListenerMutex.runExclusive(() => handler(...args)),
  );
}

type UseGetRoomEventListenersProps = Omit<UseRoomProps, "onPageUnload"> & {
  onClearReconnectionNotification?: () => void;
  onNotifyOfReconnection?: (error: Error) => void;
};

function useGetRoomEventListeners({
  onClearReconnectionNotification,
  onNotifyOfReconnection,
  onParticipantInitialConnect,
  onParticipantConnect,
  onParticipantDisconnect,
  onRoomDisconnect,
}: UseGetRoomEventListenersProps = {}): RoomEventHandlerMap {
  const setParticipants = useSetAtom(participantsAtom);
  const setDominantSpeakerId = useSetAtom(dominantSpeakerIdAtom);
  const disconnectRoom = useDisconnectRoomCallback();
  const getParticipants = useAtomCallback(
    useCallback((get) => get(participantsAtom), []),
  );
  return useMemo<RoomEventHandlerMap>(
    () =>
      mutexifyEventHandlers({
        participantConnected: (participant) => {
          if (!hasParticipant(getParticipants(), participant)) {
            sendLoggingEvents(
              TrackingEvents.REMOTE_PARTICIPANT_CONNECTED,
              {
                participant: remoteParticipantToDiagnosticInfo(participant),
              },
              { logLevel: "info", message: "New remote participant connected" },
            );
            onParticipantInitialConnect?.(participant);
          }
          setParticipants((prev) => {
            // if referentially equal, do nothing to prevent unnecessary re-renders
            if (hasParticipant(prev, participant)) return prev;

            return [
              ...filterOutByParticipantId(prev, participant.identity),
              participant,
            ];
          });
          onParticipantConnect?.(participant);
        },
        participantDisconnected: (participant) => {
          sendLoggingEvents(
            TrackingEvents.REMOTE_PARTICIPANT_DISCONNECTED,
            {
              participant: remoteParticipantToDiagnosticInfo(participant),
            },
            { logLevel: "info", message: "Remote participant disconnected" },
          );
          setParticipants((prev) =>
            filterOutByParticipantId(prev, participant.identity),
          );
          onParticipantDisconnect?.(participant);
        },
        disconnected: async (room, error) => {
          const logParams = error
            ? { error, logLevel: "error" as const }
            : { logLevel: "info" as const };
          sendLoggingEvents(
            TrackingEvents.ROOM_DISCONNECTED,
            {
              room: roomToDiagnosticInfo(room),
            },
            { ...logParams, message: "Disconnected from room" },
          );
          await disconnectRoom({
            connectionError: error,
          });
          onRoomDisconnect?.(room, error);
        },
        dominantSpeakerChanged: (participant) => {
          if (!participant) return;

          sendLoggingEvents(
            TrackingEvents.ROOM_DOMINANT_SPEAKER_CHANGED,
            {
              participant: remoteParticipantToDiagnosticInfo(participant),
            },
            { logLevel: "info", message: "Dominant speaker changed" },
          );
          setDominantSpeakerId(participant.identity);
        },
        reconnecting: (error) => {
          sendLoggingEvents(
            TrackingEvents.ROOM_RECONNECTING,
            {},
            { error, logLevel: "warn", message: "Reconnecting to room" },
          );
          onNotifyOfReconnection?.(error);
        },
        reconnected: () => {
          sendLoggingEvents(
            TrackingEvents.ROOM_RECONNECTED,
            {},
            { logLevel: "info", message: "Reconnected to room" },
          );
          onClearReconnectionNotification?.();
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onClearReconnectionNotification,
      onNotifyOfReconnection,
      onParticipantConnect,
      onParticipantDisconnect,
      onRoomDisconnect,
    ],
  );
}

export function useRoomEventListeners({
  onPageUnload,
  ...roomEventCallbacks
}: UseRoomProps = {}) {
  const room = useAtomValue(roomAtom);
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const onNotifyOfReconnection = useDebounceCallback(
    useCallback<RoomReconnectingEventHandler>(() => {
      toast.error(
        "We’re trying to reconnect you. The session may end if we’re unable to.",
        { timeout: Infinity, key: RECONNECT_TOAST_KEY, purpose: "system" },
      );
    }, []),
    RAPID_RECONNECT_THRESHOLD,
  );
  const onClearReconnectionNotification = useCallback(() => {
    // if reconnected before the notification, cancel the delayed notification
    // (this is ignored if it has already happened)
    onNotifyOfReconnection.cancel();

    toast.close(RECONNECT_TOAST_KEY);
  }, [onNotifyOfReconnection]);
  const roomEventListeners = useGetRoomEventListeners({
    ...roomEventCallbacks,
    onClearReconnectionNotification,
    onNotifyOfReconnection,
  });
  const handleExistingParticipants = useCallback(
    () => {
      const room = getRoom();
      if (!room) return;

      room.remoteParticipants.forEach(async (participant) => {
        await roomEventListeners?.participantConnected?.(participant);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roomEventListeners.participantConnected],
  );

  useEffect(
    function handleRoomEventListeners() {
      if (!room) {
        onClearReconnectionNotification();
        return;
      }

      const detachEventListenerCallbacks = Object.entries(
        roomEventListeners,
      ).map(([event, callback]) => {
        return room.on(event as RoomEvent, callback);
      });

      // Handle already connected participants
      handleExistingParticipants();

      return function cleanup() {
        for (const callback of detachEventListenerCallbacks) {
          callback();
        }
      };
    },
    [
      handleExistingParticipants,
      room,
      onClearReconnectionNotification,
      roomEventListeners,
    ],
  );

  useEffect(
    /**
     * For resiliency, periodically check for existing participants and handle
     * them.
     */
    function checkForExistingParticipantsPeriodically() {
      if (!room) return;

      const interval = setInterval(
        handleExistingParticipants,
        CHECK_FOR_EXISTING_PARTICIPANTS_INTERVAL,
      );

      return function cleanup() {
        clearInterval(interval);
      };
    },
    [room, handleExistingParticipants],
  );

  useEffect(
    function attemptDisconnectBeforePageUnload() {
      const abortController = new AbortController();
      window.addEventListener(
        "beforeunload",
        () => {
          // Callback increase chance of successfully disconnecting before the
          // tab is unloaded
          getRoom()?.disconnect();
          onPageUnload?.();
        },
        { signal: abortController.signal },
      );
      return () => abortController.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    function cleanUpTimersAndNotificationsOnUnmount() {
      // on unmount, clear any notifications behavior
      return onClearReconnectionNotification;
    },
    [onClearReconnectionNotification],
  );

  return room;
}
